/**
 * Pure edit + validation helpers for the operating-mode model (W4 editor).
 *
 * Every mutation is immutable (returns a fresh OperatingModes) and cascades so the
 * FSM stays consistent: renaming a mode rewrites the transitions and current_mode
 * that referenced it; removing a mode drops transitions that targeted it and
 * strips it from from-lists (dropping a transition only when the strip empties a
 * from-list that WAS non-empty, so a legitimate "from any" transition survives).
 *
 * `validateOperatingModes` mirrors the platform's `ValidateOperatingModes`
 * (platform-core/internal/twin/operating_modes.go) so the editor blocks a save
 * the server would reject, plus two editor-only guards (empty + duplicate mode
 * names) that a good authoring surface should catch before the upsert.
 */

import type { ModeTransition, ModeTrigger, OperatingModes } from "./operating-modes";

/** An empty model, for entering edit on an entity that declared none. */
export function emptyOperatingModes(): OperatingModes {
  return { modes: [], transitions: [] };
}

/**
 * Mirror of the platform validator + editor-only empty/duplicate guards. Returns
 * a list of human-readable problems; an empty list means the model is saveable.
 */
export function validateOperatingModes(om: OperatingModes): string[] {
  const errs: string[] = [];
  const modes = om.modes ?? [];
  if (modes.length === 0) errs.push("No modes declared.");

  const seen = new Set<string>();
  for (const m of modes) {
    const name = m.trim();
    if (!name) errs.push("A mode has an empty name.");
    else if (seen.has(name)) errs.push(`Duplicate mode "${name}".`);
    seen.add(name);
  }

  const modeSet = new Set(modes);
  if (om.current_mode && !modeSet.has(om.current_mode)) {
    errs.push(`Current mode "${om.current_mode}" is not a declared mode.`);
  }

  (om.transitions ?? []).forEach((t, i) => {
    const label = t.on_event?.trim() ? `"${t.on_event}"` : `#${i + 1}`;
    if (!t.on_event?.trim()) errs.push(`Transition ${i + 1}: event name is empty.`);
    if (!t.to?.trim()) errs.push(`Transition ${label}: target mode is empty.`);
    else if (!modeSet.has(t.to))
      errs.push(`Transition ${label}: target "${t.to}" is not a declared mode.`);
    for (const f of t.from ?? []) {
      if (!modeSet.has(f))
        errs.push(`Transition ${label}: source "${f}" is not a declared mode.`);
    }
  });

  return errs;
}

/** Append a mode (caller trims/dedupes for UX; validation is the real gate). */
export function addMode(om: OperatingModes, name: string): OperatingModes {
  return { ...om, modes: [...(om.modes ?? []), name] };
}

function renameInTransition(t: ModeTransition, old: string, next: string): ModeTransition {
  return {
    ...t,
    to: t.to === old ? next : t.to,
    from: (t.from ?? []).map((f) => (f === old ? next : f)),
  };
}

/** Rename modes[index]; cascade the change into transitions + current_mode. */
export function renameModeAt(om: OperatingModes, index: number, next: string): OperatingModes {
  const modes = [...(om.modes ?? [])];
  if (index < 0 || index >= modes.length) return om;
  const old = modes[index];
  modes[index] = next;
  if (old === next) return { ...om, modes };
  const transitions = (om.transitions ?? []).map((t) => renameInTransition(t, old, next));
  const current_mode = om.current_mode === old ? next : om.current_mode;
  return { ...om, modes, transitions, current_mode };
}

/**
 * Remove modes[index]; cascade so no transition dangles. A transition targeting
 * the removed mode is dropped; the mode is stripped from every from-list; a
 * transition whose from-list was non-empty and became empty is dropped (a
 * transition that was already "from any" is left untouched).
 */
export function removeModeAt(om: OperatingModes, index: number): OperatingModes {
  const modes = [...(om.modes ?? [])];
  if (index < 0 || index >= modes.length) return om;
  const removed = modes[index];
  modes.splice(index, 1);

  const transitions = (om.transitions ?? [])
    .filter((t) => t.to !== removed)
    .map((t) => {
      const hadFrom = (t.from?.length ?? 0) > 0;
      const from = (t.from ?? []).filter((f) => f !== removed);
      return { t, from, hadFrom };
    })
    .filter(({ from, hadFrom }) => !hadFrom || from.length > 0)
    .map(({ t, from }) => ({ ...t, from }));

  const current_mode = om.current_mode === removed ? undefined : om.current_mode;
  return { ...om, modes, transitions, current_mode };
}

/** Set (or clear, with undefined) the declared current mode. */
export function setCurrentMode(om: OperatingModes, mode: string | undefined): OperatingModes {
  return { ...om, current_mode: mode || undefined };
}

// --- Transitions -------------------------------------------------------------

function mapTransitionAt(
  om: OperatingModes,
  index: number,
  fn: (t: ModeTransition) => ModeTransition,
): OperatingModes {
  const transitions = (om.transitions ?? []).map((t, i) => (i === index ? fn(t) : t));
  return { ...om, transitions };
}

/** A fresh transition: from-any, targeting the first declared mode, unnamed event. */
export function blankTransition(om: OperatingModes): ModeTransition {
  return { from: [], to: om.modes?.[0] ?? "", on_event: "" };
}

export function addTransition(om: OperatingModes): OperatingModes {
  return { ...om, transitions: [...(om.transitions ?? []), blankTransition(om)] };
}

export function removeTransitionAt(om: OperatingModes, index: number): OperatingModes {
  return { ...om, transitions: (om.transitions ?? []).filter((_, i) => i !== index) };
}

/** Merge a patch (to / on_event) into transitions[index]. */
export function updateTransitionAt(
  om: OperatingModes,
  index: number,
  patch: Partial<Pick<ModeTransition, "to" | "on_event">>,
): OperatingModes {
  return mapTransitionAt(om, index, (t) => ({ ...t, ...patch }));
}

/** Toggle a mode in transitions[index].from (empty from = "from any state"). */
export function toggleFromAt(om: OperatingModes, index: number, mode: string): OperatingModes {
  return mapTransitionAt(om, index, (t) => {
    const from = t.from ?? [];
    return {
      ...t,
      from: from.includes(mode) ? from.filter((f) => f !== mode) : [...from, mode],
    };
  });
}

/**
 * Set (or clear, with undefined) the predicate on transitions[index]. Clearing
 * drops the whole trigger (no predicate = a manual / derived event); setting one
 * preserves any fields already present.
 */
export function setTriggerPredicateAt(
  om: OperatingModes,
  index: number,
  predicate: string | undefined,
): OperatingModes {
  return mapTransitionAt(om, index, (t) =>
    predicate
      ? { ...t, trigger: { ...(t.trigger ?? {}), predicate } }
      : { ...t, trigger: undefined },
  );
}

/**
 * Merge a patch into transitions[index].trigger (a no-op when no predicate is set
 * yet). A field set to undefined clears on serialize (JSON drops undefined).
 */
export function updateTriggerAt(
  om: OperatingModes,
  index: number,
  patch: Partial<ModeTrigger>,
): OperatingModes {
  return mapTransitionAt(om, index, (t) =>
    t.trigger ? { ...t, trigger: { ...t.trigger, ...patch } } : t,
  );
}

/**
 * Editor-side trigger sanity, separate from the FSM mirror: an analog crossing
 * predicate needs a level to fire. (coil/count predicates need no numeric field.)
 */
export function validateTriggers(om: OperatingModes): string[] {
  const errs: string[] = [];
  (om.transitions ?? []).forEach((t, i) => {
    const p = t.trigger?.predicate;
    if (!p) return;
    const label = t.on_event?.trim() ? `"${t.on_event}"` : `#${i + 1}`;
    if ((p === "analog_cross_above" || p === "analog_cross_below") && t.trigger?.level === undefined) {
      errs.push(`Transition ${label}: ${p} needs a level.`);
    }
  });
  return errs;
}
